import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BadgeCheck,
  CalendarClock,
  Pencil,
  Plus,
  Trash2,
  UserCheck,
  UserMinus,
  UserX,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { KpiCard, Panel, DataTable, type DataColumn } from "@/components/admin/analytics/AnalyticsKit";
import { DriverProfileDrawer } from "@/components/admin/delivery/DriverProfileDrawer";
import {
  FieldGroup,
  NotesField,
  NumberField,
  SelectField,
  SwitchField,
  TextField,
} from "@/components/admin/delivery/form-bits";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toneClass } from "@/lib/admin/order-badges";
import {
  deleteDeliveryPartner,
  listDeliveryPartners,
  listDeliveryVehicles,
  listDeliveryZones,
  saveDeliveryPartner,
} from "@/lib/admin/delivery.functions";
import {
  DRIVER_STATUSES,
  DRIVER_STATUS_LABEL,
  EMPLOYMENT_TYPES,
  EMPLOYMENT_TYPE_LABEL,
  GENDERS,
  SHIFTS,
  SHIFT_LABEL,
  isExpired,
  partnerSchema,
  statusTone,
  type DeliveryPartner,
  type PartnerInput,
} from "@/lib/admin/delivery.schemas";

export const Route = createFileRoute("/_admin/admin/delivery-partners")({
  head: () => ({
    meta: [
      { title: "Delivery Partners — Admin" },
      {
        name: "description",
        content:
          "Master data for every delivery partner: profile, employment, documents, zone, shift and performance.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: DeliveryPartnersPage,
});

function emptyForm(): PartnerInput {
  return {
    employee_code: "",
    full_name: "",
    gender: "male",
    dob: null,
    blood_group: "",
    photo_url: "",
    address_line: "",
    city: "Mumbai",
    state: "Maharashtra",
    pincode: "",
    emergency_contact_name: "",
    emergency_phone: "",
    phone: "",
    whatsapp: "",
    email: "",
    joining_date: null,
    employment_type: "full_time",
    salary_paise: 0,
    bank_account_name: "",
    bank_account_number: "",
    bank_ifsc: "",
    licence_number: "",
    licence_expiry: null,
    aadhaar_number: "",
    pan_number: "",
    zone_id: null,
    vehicle_id: null,
    shift: "morning",
    max_deliveries: 25,
    shift_start: "08:00",
    shift_end: "16:00",
    weekly_off: "sunday",
    status: "available",
    is_active: true,
    rating: 0,
    completed_deliveries: 0,
    cancelled_deliveries: 0,
    failed_deliveries: 0,
    late_deliveries: 0,
    avg_delivery_minutes: 0,
    revenue_handled_paise: 0,
    doc_licence_url: "",
    doc_aadhaar_url: "",
    doc_pan_url: "",
    doc_insurance_url: "",
    doc_police_verification_url: "",
    notes: "",
  };
}

function fromRow(p: DeliveryPartner): PartnerInput {
  return {
    ...emptyForm(),
    employee_code: p.employee_code,
    full_name: p.full_name,
    gender: (p.gender as PartnerInput["gender"]) ?? "male",
    dob: p.dob,
    blood_group: p.blood_group,
    photo_url: p.photo_url,
    address_line: p.address_line,
    city: p.city,
    state: p.state,
    pincode: p.pincode,
    emergency_contact_name: p.emergency_contact_name,
    emergency_phone: p.emergency_phone,
    phone: p.phone,
    whatsapp: p.whatsapp,
    email: p.email,
    joining_date: p.joining_date,
    employment_type: (p.employment_type as PartnerInput["employment_type"]) ?? "full_time",
    salary_paise: p.salary_paise,
    bank_account_name: p.bank_account_name,
    bank_account_number: p.bank_account_number,
    bank_ifsc: p.bank_ifsc,
    licence_number: p.licence_number,
    licence_expiry: p.licence_expiry,
    aadhaar_number: p.aadhaar_number,
    pan_number: p.pan_number,
    zone_id: p.zone_id,
    vehicle_id: p.vehicle_id,
    shift: p.shift,
    max_deliveries: p.max_deliveries,
    shift_start: p.shift_start,
    shift_end: p.shift_end,
    weekly_off: p.weekly_off,
    status: p.status,
    is_active: p.is_active,
    rating: p.rating,
    completed_deliveries: p.completed_deliveries,
    cancelled_deliveries: p.cancelled_deliveries,
    failed_deliveries: p.failed_deliveries,
    late_deliveries: p.late_deliveries,
    avg_delivery_minutes: p.avg_delivery_minutes,
    revenue_handled_paise: p.revenue_handled_paise,
    doc_licence_url: p.doc_licence_url,
    doc_aadhaar_url: p.doc_aadhaar_url,
    doc_pan_url: p.doc_pan_url,
    doc_insurance_url: p.doc_insurance_url,
    doc_police_verification_url: p.doc_police_verification_url,
    notes: p.notes,
  };
}

function DeliveryPartnersPage() {
  const queryClient = useQueryClient();
  const listFn = useServerFn(listDeliveryPartners);
  const zonesFn = useServerFn(listDeliveryZones);
  const vehiclesFn = useServerFn(listDeliveryVehicles);
  const saveFn = useServerFn(saveDeliveryPartner);
  const deleteFn = useServerFn(deleteDeliveryPartner);

  const partners = useQuery({
    queryKey: ["admin", "delivery", "partners"],
    queryFn: () => listFn(),
    staleTime: 10_000,
  });
  const zones = useQuery({
    queryKey: ["admin", "delivery", "zones"],
    queryFn: () => zonesFn(),
    staleTime: 60_000,
  });
  const vehicles = useQuery({
    queryKey: ["admin", "delivery", "vehicles"],
    queryFn: () => vehiclesFn(),
    staleTime: 60_000,
  });

  const [editing, setEditing] = useState<DeliveryPartner | "new" | null>(null);
  const [form, setForm] = useState<PartnerInput>(emptyForm());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [profile, setProfile] = useState<DeliveryPartner | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<DeliveryPartner | null>(null);

  useEffect(() => {
    if (editing === "new") setForm(emptyForm());
    else if (editing) setForm(fromRow(editing));
    setErrors({});
  }, [editing]);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["admin", "delivery"] });

  const save = useMutation({
    mutationFn: (v: { id: string | null; patch: PartnerInput }) => saveFn({ data: v as never }),
    onSuccess: async () => {
      toast.success("Delivery partner saved");
      setEditing(null);
      await invalidate();
    },
    onError: (e: Error) =>
      toast.error(
        e.message === "duplicate" ? "That employee ID already exists." : "Couldn't save partner.",
      ),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } as never }),
    onSuccess: async () => {
      toast.success("Delivery partner removed");
      setConfirmDelete(null);
      await invalidate();
    },
    onError: () => toast.error("Couldn't remove partner."),
  });

  const rows = useMemo(() => partners.data ?? [], [partners.data]);
  const kpi = useMemo(() => {
    const total = rows.length;
    const activeToday = rows.filter((r) => r.is_active && r.status !== "on_leave").length;
    const onLeave = rows.filter((r) => r.status === "on_leave").length;
    const available = rows.filter((r) => r.status === "available" && r.is_active).length;
    const busy = rows.filter((r) => r.status === "busy").length;
    const inactive = rows.filter((r) => !r.is_active || r.status === "inactive").length;
    return { total, activeToday, onLeave, available, busy, inactive };
  }, [rows]);

  const zoneOptions = [
    { value: "none", label: "Unassigned" },
    ...(zones.data ?? []).map((z) => ({ value: z.id, label: z.zone_name })),
  ];
  const vehicleOptions = [
    { value: "none", label: "No vehicle" },
    ...(vehicles.data ?? []).map((v) => ({
      value: v.id,
      label: `${v.vehicle_number} · ${v.vehicle_name}`,
    })),
  ];

  const columns: Array<DataColumn<DeliveryPartner>> = [
    {
      key: "photo",
      header: "Photo",
      render: (r) =>
        r.photo_url ? (
          <img
            src={r.photo_url}
            alt=""
            loading="lazy"
            className="h-8 w-8 rounded-full border border-border object-cover"
          />
        ) : (
          <div className="grid h-8 w-8 place-items-center rounded-full border border-border bg-muted text-[10px] font-semibold text-muted-foreground">
            {r.full_name.slice(0, 2).toUpperCase()}
          </div>
        ),
      exportValue: (r) => r.photo_url,
    },
    {
      key: "code",
      header: "Employee ID",
      render: (r) => <span className="font-mono text-xs">{r.employee_code}</span>,
      sortValue: (r) => r.employee_code,
      exportValue: (r) => r.employee_code,
    },
    {
      key: "name",
      header: "Driver name",
      render: (r) => (
        <button
          type="button"
          onClick={() => setProfile(r)}
          className="font-medium text-foreground underline-offset-4 hover:underline"
        >
          {r.full_name}
        </button>
      ),
      sortValue: (r) => r.full_name,
      exportValue: (r) => r.full_name,
    },
    { key: "phone", header: "Phone", render: (r) => r.phone, exportValue: (r) => r.phone },
    {
      key: "whatsapp",
      header: "WhatsApp",
      render: (r) => r.whatsapp || "—",
      exportValue: (r) => r.whatsapp,
    },
    {
      key: "email",
      header: "Email",
      render: (r) => <span className="text-xs">{r.email || "—"}</span>,
      exportValue: (r) => r.email,
    },
    {
      key: "vehicle",
      header: "Vehicle",
      render: (r) => r.vehicle_number || "—",
      exportValue: (r) => r.vehicle_number,
    },
    {
      key: "zone",
      header: "Zone",
      render: (r) => r.zone_name || "—",
      sortValue: (r) => r.zone_name,
      exportValue: (r) => r.zone_name,
    },
    {
      key: "shift",
      header: "Shift",
      render: (r) => SHIFT_LABEL[r.shift],
      exportValue: (r) => SHIFT_LABEL[r.shift],
    },
    {
      key: "status",
      header: "Current status",
      render: (r) => (
        <Badge className={toneClass(statusTone(r.status))}>{DRIVER_STATUS_LABEL[r.status]}</Badge>
      ),
      sortValue: (r) => r.status,
      exportValue: (r) => DRIVER_STATUS_LABEL[r.status],
    },
    {
      key: "availability",
      header: "Availability",
      render: (r) =>
        r.is_active ? (
          <span className="text-xs text-muted-foreground">
            {r.shift_start}–{r.shift_end}
          </span>
        ) : (
          <span className="text-xs text-destructive">Inactive</span>
        ),
      exportValue: (r) => (r.is_active ? `${r.shift_start}-${r.shift_end}` : "inactive"),
    },
    {
      key: "joining",
      header: "Joining date",
      render: (r) => r.joining_date ?? "—",
      sortValue: (r) => r.joining_date ?? "",
      exportValue: (r) => r.joining_date ?? "",
    },
    {
      key: "rating",
      header: "Rating",
      align: "right",
      render: (r) => r.rating.toFixed(2),
      sortValue: (r) => r.rating,
      exportValue: (r) => r.rating,
    },
    {
      key: "completed",
      header: "Completed",
      align: "right",
      render: (r) => r.completed_deliveries,
      sortValue: (r) => r.completed_deliveries,
      exportValue: (r) => r.completed_deliveries,
    },
    {
      key: "pending",
      header: "Pending today",
      align: "right",
      render: (r) => r.today_pending,
      sortValue: (r) => r.today_pending,
      exportValue: (r) => r.today_pending,
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (r) => (
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="ghost" onClick={() => setProfile(r)}>
            View
          </Button>
          <Button size="sm" variant="outline" onClick={() => setEditing(r)}>
            <Pencil className="h-3.5 w-3.5" aria-hidden />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(r)}>
            <Trash2 className="h-3.5 w-3.5 text-destructive" aria-hidden />
          </Button>
        </div>
      ),
      exportValue: () => "",
    },
  ];

  const submit = () => {
    const parsed = partnerSchema.safeParse(form);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) next[String(issue.path[0])] = issue.message;
      setErrors(next);
      toast.error("Please fix the highlighted fields.");
      return;
    }
    save.mutate({ id: editing === "new" || editing === null ? null : editing.id, patch: parsed.data });
  };

  const set = <K extends keyof PartnerInput>(key: K, value: PartnerInput[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  return (
    <div>
      <AdminPageHeader
        title="Delivery Partners"
        description="The single source of truth for delivery staff. Dispatch, Deliveries, Reports and Analytics all read from these records."
      />

      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <KpiCard label="Total drivers" value={String(kpi.total)} tone="info" icon={Users} loading={partners.isLoading} />
        <KpiCard label="Active today" value={String(kpi.activeToday)} tone="success" icon={UserCheck} loading={partners.isLoading} />
        <KpiCard label="On leave" value={String(kpi.onLeave)} tone="warn" icon={CalendarClock} loading={partners.isLoading} />
        <KpiCard label="Available" value={String(kpi.available)} tone="teal" icon={BadgeCheck} loading={partners.isLoading} />
        <KpiCard label="Busy" value={String(kpi.busy)} tone="purple" icon={UserMinus} loading={partners.isLoading} />
        <KpiCard label="Inactive" value={String(kpi.inactive)} tone="danger" icon={UserX} loading={partners.isLoading} />
      </div>

      <Panel title="Delivery partner master" subtitle={`${rows.length} records`}>
        <div className="mb-3 flex justify-end">
          <Button size="sm" className="gap-1.5" onClick={() => setEditing("new")}>
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Add delivery partner
          </Button>
        </div>
        <DataTable
          rows={rows}
          columns={columns}
          loading={partners.isLoading}
          searchOn={(r) =>
            `${r.full_name} ${r.employee_code} ${r.phone} ${r.zone_name} ${r.vehicle_number} ${r.email}`
          }
          pageSize={10}
          exportName="delivery-partners"
          importModule="delivery-partners"
          minWidth={1100}
        />
      </Panel>

      <DriverProfileDrawer partner={profile} onOpenChange={(o) => !o && setProfile(null)} />

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[88vh] max-w-5xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing === "new" ? "New delivery partner" : "Edit delivery partner"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid gap-3">
            <FieldGroup title="Personal details">
              <TextField
                label="Employee ID"
                value={form.employee_code}
                onChange={(v) => set("employee_code", v)}
                error={errors["employee_code"]}
              />
              <TextField
                label="Full name"
                value={form.full_name}
                onChange={(v) => set("full_name", v)}
                error={errors["full_name"]}
              />
              <TextField
                label="Photo URL"
                value={form.photo_url}
                onChange={(v) => set("photo_url", v)}
              />
              <SelectField
                label="Gender"
                value={form.gender}
                onChange={(v) => set("gender", v as PartnerInput["gender"])}
                options={GENDERS.map((g) => ({ value: g, label: g[0]!.toUpperCase() + g.slice(1) }))}
              />
              <TextField
                label="Date of birth"
                type="date"
                value={form.dob ?? ""}
                onChange={(v) => set("dob", v || null)}
              />
              <TextField
                label="Blood group"
                value={form.blood_group}
                onChange={(v) => set("blood_group", v)}
              />
              <TextField
                label="Address"
                value={form.address_line}
                onChange={(v) => set("address_line", v)}
                className="sm:col-span-2"
              />
              <TextField label="City" value={form.city} onChange={(v) => set("city", v)} />
              <TextField label="State" value={form.state} onChange={(v) => set("state", v)} />
              <TextField label="Pincode" value={form.pincode} onChange={(v) => set("pincode", v)} />
              <TextField
                label="Emergency contact"
                value={form.emergency_contact_name}
                onChange={(v) => set("emergency_contact_name", v)}
              />
              <TextField
                label="Emergency phone"
                value={form.emergency_phone}
                onChange={(v) => set("emergency_phone", v)}
              />
            </FieldGroup>

            <FieldGroup title="Contact">
              <TextField
                label="Mobile"
                value={form.phone}
                onChange={(v) => set("phone", v)}
                error={errors["phone"]}
              />
              <TextField
                label="WhatsApp"
                value={form.whatsapp}
                onChange={(v) => set("whatsapp", v)}
              />
              <TextField label="Email" value={form.email} onChange={(v) => set("email", v)} />
            </FieldGroup>

            <FieldGroup title="Employment">
              <TextField
                label="Joining date"
                type="date"
                value={form.joining_date ?? ""}
                onChange={(v) => set("joining_date", v || null)}
              />
              <SelectField
                label="Employment type"
                value={form.employment_type}
                onChange={(v) => set("employment_type", v as PartnerInput["employment_type"])}
                options={EMPLOYMENT_TYPES.map((t) => ({
                  value: t,
                  label: EMPLOYMENT_TYPE_LABEL[t] ?? t,
                }))}
              />
              <NumberField
                label="Monthly salary"
                suffix="₹"
                value={Math.round(form.salary_paise / 100)}
                onChange={(v) => set("salary_paise", Math.max(0, Math.round(v * 100)))}
              />
              <TextField
                label="Bank account name"
                value={form.bank_account_name}
                onChange={(v) => set("bank_account_name", v)}
              />
              <TextField
                label="Bank account number"
                value={form.bank_account_number}
                onChange={(v) => set("bank_account_number", v)}
              />
              <TextField label="IFSC" value={form.bank_ifsc} onChange={(v) => set("bank_ifsc", v)} />
              <TextField
                label="Driving licence number"
                value={form.licence_number}
                onChange={(v) => set("licence_number", v)}
              />
              <TextField
                label="Licence expiry"
                type="date"
                value={form.licence_expiry ?? ""}
                onChange={(v) => set("licence_expiry", v || null)}
              />
              <TextField
                label="Aadhaar"
                value={form.aadhaar_number}
                onChange={(v) => set("aadhaar_number", v)}
              />
              <TextField label="PAN" value={form.pan_number} onChange={(v) => set("pan_number", v)} />
            </FieldGroup>

            <FieldGroup title="Delivery information">
              <SelectField
                label="Assigned zone"
                value={form.zone_id ?? "none"}
                onChange={(v) => set("zone_id", v === "none" ? null : v)}
                options={zoneOptions}
              />
              <SelectField
                label="Working shift"
                value={form.shift}
                onChange={(v) => set("shift", v as PartnerInput["shift"])}
                options={SHIFTS.map((sh) => ({ value: sh, label: SHIFT_LABEL[sh] }))}
              />
              <SelectField
                label="Vehicle"
                value={form.vehicle_id ?? "none"}
                onChange={(v) => set("vehicle_id", v === "none" ? null : v)}
                options={vehicleOptions}
              />
              <NumberField
                label="Max deliveries"
                value={form.max_deliveries}
                onChange={(v) => set("max_deliveries", v)}
              />
              <TextField
                label="Default start time"
                type="time"
                value={form.shift_start}
                onChange={(v) => set("shift_start", v)}
              />
              <TextField
                label="Default end time"
                type="time"
                value={form.shift_end}
                onChange={(v) => set("shift_end", v)}
              />
              <TextField
                label="Weekly off"
                value={form.weekly_off}
                onChange={(v) => set("weekly_off", v)}
              />
              <SelectField
                label="Status"
                value={form.status}
                onChange={(v) => set("status", v as PartnerInput["status"])}
                options={DRIVER_STATUSES.map((st) => ({ value: st, label: DRIVER_STATUS_LABEL[st] }))}
              />
              <SwitchField
                label="Active"
                hint="Inactive drivers never appear in Dispatch"
                checked={form.is_active}
                onChange={(v) => set("is_active", v)}
              />
            </FieldGroup>

            <FieldGroup title="Performance">
              <NumberField
                label="Completed deliveries"
                value={form.completed_deliveries}
                onChange={(v) => set("completed_deliveries", v)}
              />
              <NumberField
                label="Cancelled"
                value={form.cancelled_deliveries}
                onChange={(v) => set("cancelled_deliveries", v)}
              />
              <NumberField
                label="Failed"
                value={form.failed_deliveries}
                onChange={(v) => set("failed_deliveries", v)}
              />
              <NumberField
                label="Customer rating"
                step={0.01}
                value={form.rating}
                onChange={(v) => set("rating", v)}
              />
              <NumberField
                label="Average delivery time"
                suffix="min"
                value={form.avg_delivery_minutes}
                onChange={(v) => set("avg_delivery_minutes", v)}
              />
              <NumberField
                label="Late deliveries"
                value={form.late_deliveries}
                onChange={(v) => set("late_deliveries", v)}
              />
              <NumberField
                label="Revenue handled"
                suffix="₹"
                value={Math.round(form.revenue_handled_paise / 100)}
                onChange={(v) => set("revenue_handled_paise", Math.max(0, Math.round(v * 100)))}
              />
            </FieldGroup>

            <FieldGroup title="Documents" hint="Paste a stored document link for each record.">
              <TextField
                label="Driving licence"
                value={form.doc_licence_url}
                onChange={(v) => set("doc_licence_url", v)}
              />
              <TextField
                label="Aadhaar"
                value={form.doc_aadhaar_url}
                onChange={(v) => set("doc_aadhaar_url", v)}
              />
              <TextField label="PAN" value={form.doc_pan_url} onChange={(v) => set("doc_pan_url", v)} />
              <TextField
                label="Insurance"
                value={form.doc_insurance_url}
                onChange={(v) => set("doc_insurance_url", v)}
              />
              <TextField
                label="Police verification"
                value={form.doc_police_verification_url}
                onChange={(v) => set("doc_police_verification_url", v)}
              />
              <NotesField value={form.notes} onChange={(v) => set("notes", v)} />
            </FieldGroup>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={save.isPending}>
              {save.isPending ? "Saving…" : "Save partner"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDelete !== null} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove delivery partner?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.full_name} will be removed from the master list. Past dispatch records
              keep the printed driver name.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => confirmDelete && remove.mutate(confirmDelete.id)}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {rows.some((r) => isExpired(r.licence_expiry)) ? (
        <p className="mt-4 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
          Some drivers have an expired driving licence. They are blocked from Dispatch until the
          licence is renewed.
        </p>
      ) : null}
    </div>
  );
}
